import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, MagnifyingGlass, CaretDown } from "@phosphor-icons/react";
import CommandPalette from "@/components/search/CommandPalette";
import { GoldButton } from "@/components/common/ui";
import BhtLogo from "@/components/common/BhtLogo";

const MEGA = (t, lang) => ({
  product: {
    label: t("nav.product"),
    columns: [
      {
        heading: t("nav.product"),
        items: [
          { title: t("nav.product"), desc: t("brand.tagline"), to: `/${lang}/bht-one` },
          { title: t("nav.platform"), desc: "Architecture, APIs, security & scale", to: `/${lang}/platform` },
          { title: t("nav.why"), desc: "The case for a Hospitality OS", to: `/${lang}/why-bht-one` },
        ],
      },
      {
        heading: t("nav.modules"),
        items: [
          { title: "Reception", desc: "AI-assisted check-in & mobile keys", to: `/${lang}/modules/reception` },
          { title: "Housekeeping", desc: "Real-time status, AI dispatch", to: `/${lang}/modules/housekeeping` },
          { title: "Restaurant", desc: "POS, KDS, allergen intelligence", to: `/${lang}/modules/restaurant` },
          { title: "AI Copilot", desc: "The staff-facing AI copilot", to: `/${lang}/modules/ai-copilot` },
          { title: "Analytics", desc: "Cross-module dashboards & forecasts", to: `/${lang}/modules/analytics` },
          { title: "All modules", desc: "See the full suite", to: `/${lang}/modules` },
        ],
      },
    ],
  },
  company: {
    label: lang === "nl" ? "Bedrijf" : "Company",
    columns: [
      {
        heading: lang === "nl" ? "Wie we zijn" : "Who we are",
        items: [
          { title: lang === "nl" ? "Bedrijf" : "Company", desc: lang === "nl" ? "Ons verhaal, founders & waarden" : "Our story, founders & values", to: `/${lang}/company` },
          { title: lang === "nl" ? "Manifesto" : "Manifesto", desc: lang === "nl" ? "Onze filosofie op hoteltechnologie" : "Our philosophy on hotel technology", to: `/${lang}/manifesto` },
          { title: lang === "nl" ? "Case studies" : "Case studies", desc: lang === "nl" ? "Echte hotels, echte cijfers" : "Real hotels, real numbers", to: `/${lang}/case-studies` },
          { title: lang === "nl" ? "Community" : "Community", desc: lang === "nl" ? "Events, webinars & user groups" : "Events, webinars & user groups", to: `/${lang}/community` },
        ],
      },
      {
        heading: lang === "nl" ? "Praat met ons" : "Talk to us",
        items: [
          { title: lang === "nl" ? "Werken bij BHT" : "Careers", desc: lang === "nl" ? "Bouw hospitality-tech mee" : "Help us build hospitality tech", to: `/${lang}/careers` },
          { title: lang === "nl" ? "Media & press" : "Media & press", desc: lang === "nl" ? "Nieuws en aankondigingen" : "News and announcements", to: `/${lang}/media` },
          { title: lang === "nl" ? "Investeerders" : "Investors", desc: lang === "nl" ? "Voor institutionele partners" : "For institutional partners", to: `/${lang}/investors` },
          { title: lang === "nl" ? "Partners" : "Partners", desc: lang === "nl" ? "Partner Program in ontwikkeling" : "Partner Program in development", to: `/${lang}/partners` },
        ],
      },
    ],
  },
});

export default function Nav() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 16);
    s();
    window.addEventListener("scroll", s, { passive: true });
    return () => window.removeEventListener("scroll", s);
  }, []);

  useEffect(() => {
    setMobile(false);
    setOpen(null);
  }, [location.pathname]);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(null);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const switchLang = () => {
    const other = lang === "en" ? "nl" : "en";
    const rest = location.pathname.replace(/^\/(en|nl)/, "");
    window.location.assign(`/${other}${rest || ""}`);
  };

  const mega = MEGA(t, lang);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
          scrolled ? "glass-strong border-b border-white/[0.06]" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
          <Link to={`/${lang}`} className="flex items-center gap-3" data-testid="nav-logo">
            <BhtLogo className="h-8 w-8" />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-lg tracking-tight">BHT Solutions</span>
              <span className="eyebrow -mt-0.5 text-[9px]">{lang === "nl" ? "De digitale hotelmanager" : "The digital hotel manager"}</span>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            <MegaTrigger label={mega.product.label} open={open === "product"} onOpen={() => setOpen(open === "product" ? null : "product")} testId="nav-product" />
            <NavItem to={`/${lang}/solutions`} label={lang === "nl" ? "Solutions" : "Solutions"} testId="nav-solutions" />
            <NavItem to={`/${lang}/industries`} label={lang === "nl" ? "Industries" : "Industries"} testId="nav-industries" />
            <NavItem to={`/${lang}/demo`} label={lang === "nl" ? "Demo" : "Demo"} testId="nav-demo-interactive" />
            <NavItem to={`/${lang}/pricing`} label={lang === "nl" ? "Prijzen" : "Pricing"} testId="nav-pricing" />
            <MegaTrigger label={mega.company.label} open={open === "company"} onOpen={() => setOpen(open === "company" ? null : "company")} testId="nav-company" />
            <NavItem to={`/${lang}/trust`} label={lang === "nl" ? "Trust" : "Trust"} testId="nav-trust" />
            <NavItem to={`/${lang}/knowledge`} label={t("nav.knowledge")} testId="nav-knowledge" />
            <NavItem to={`/${lang}/contact`} label={t("nav.contact")} testId="nav-contact" />
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCmdOpen(true)}
              className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/70 hover:border-bht-gold/50 hover:text-bht-gold md:inline-flex"
              data-testid="nav-search"
              aria-label={t("nav.search")}
            >
              <MagnifyingGlass size={14} weight="regular" />
              <span className="font-sans">{t("nav.search")}</span>
              <kbd className="ml-1 rounded border border-white/10 bg-black/40 px-1.5 py-0.5 font-mono text-[10px] text-white/60">⌘K</kbd>
            </button>
            <button
              onClick={switchLang}
              className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-white/70 hover:border-bht-gold/50 hover:text-bht-gold"
              data-testid="nav-lang-switch"
              aria-label="Switch language"
            >
              {lang === "en" ? "NL" : "EN"}
            </button>
            <div className="hidden sm:inline-flex">
              <GoldButton to={`/${lang}/book-demo`} size="sm" testId="nav-cta-demo">
                {t("nav.demo")}
              </GoldButton>
            </div>
            <button
              className="ml-1 rounded-md border border-white/10 p-2 text-white/80 hover:bg-white/5 lg:hidden"
              onClick={() => setMobile((v) => !v)}
              aria-label={mobile ? t("nav.close") : t("nav.menu")}
              data-testid="nav-mobile-toggle"
            >
              {mobile ? <X size={18} /> : <List size={18} />}
            </button>
          </div>
        </div>

        {/* Mega menu */}
        <AnimatePresence>
          {open && mega[open] && (
            <motion.div
              key={`mega-${open}`}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="glass-strong absolute left-0 right-0 top-full border-t border-white/[0.06]"
              onMouseLeave={() => setOpen(null)}
            >
              <div className="mx-auto grid max-w-[1400px] gap-10 px-10 py-10 md:grid-cols-2">
                {mega[open].columns.map((col) => (
                  <div key={col.heading}>
                    <div className="eyebrow mb-4">{col.heading}</div>
                    <ul className="space-y-1">
                      {col.items.map((it) => (
                        <li key={it.to}>
                          <Link
                            to={it.to}
                            className="group flex items-start justify-between gap-6 rounded-xl px-3 py-3 transition-colors duration-300 hover:bg-white/[0.04]"
                            data-testid={`mega-${it.title.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <div>
                              <div className="font-sans text-[15px] text-white transition-colors group-hover:text-bht-gold">{it.title}</div>
                              <div className="mt-0.5 text-xs text-white/50">{it.desc}</div>
                            </div>
                            <span className="mt-2 h-px w-8 shrink-0 bg-white/10 transition-colors group-hover:bg-bht-gold/70" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobile && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="glass-strong overflow-hidden border-t border-white/[0.06] lg:hidden"
            >
              <div className="flex flex-col px-6 py-6">
                {[
                  { label: t("nav.product"), to: `/${lang}/bht-one` },
                  { label: t("nav.platform"), to: `/${lang}/platform` },
                  { label: t("nav.why"), to: `/${lang}/why-bht-one` },
                  { label: t("nav.modules"), to: `/${lang}/modules` },
                  { label: "Solutions", to: `/${lang}/solutions` },
                  { label: "Industries", to: `/${lang}/industries` },
                  { label: "Trust", to: `/${lang}/trust` },
                  { label: "Demo", to: `/${lang}/demo` },
                  { label: lang === "nl" ? "Prijzen" : "Pricing", to: `/${lang}/pricing` },
                  { label: lang === "nl" ? "Bedrijf" : "Company", to: `/${lang}/company` },
                  { label: lang === "nl" ? "Manifesto" : "Manifesto", to: `/${lang}/manifesto` },
                  { label: lang === "nl" ? "Case studies" : "Case studies", to: `/${lang}/case-studies` },
                  { label: lang === "nl" ? "Werken bij BHT" : "Careers", to: `/${lang}/careers` },
                  { label: lang === "nl" ? "Investeerders" : "Investors", to: `/${lang}/investors` },
                  { label: lang === "nl" ? "Partners" : "Partners", to: `/${lang}/partners` },
                  { label: t("nav.knowledge"), to: `/${lang}/knowledge` },
                  { label: t("nav.contact"), to: `/${lang}/contact` },
                ].map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    className="border-b border-white/5 py-4 text-lg font-serif tracking-tight text-white hover:text-bht-gold"
                    data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {it.label}
                  </NavLink>
                ))}
                <div className="mt-6 flex items-center gap-3">
                  <GoldButton to={`/${lang}/book-demo`} size="sm" testId="mobile-nav-cta">
                    {t("nav.demo")}
                  </GoldButton>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </>
  );
}

function NavItem({ to, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `relative rounded-full px-3.5 py-2 text-[14px] font-medium transition-colors duration-300 ${
          isActive ? "text-bht-gold" : "text-white/75 hover:text-white"
        }`
      }
      data-testid={testId}
    >
      {label}
    </NavLink>
  );
}

function MegaTrigger({ label, open, onOpen, testId }) {
  return (
    <button
      onClick={onOpen}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[14px] font-medium transition-colors duration-300 ${
        open ? "text-bht-gold" : "text-white/75 hover:text-white"
      }`}
      data-testid={testId}
      aria-expanded={open}
    >
      {label}
      <CaretDown size={12} weight="bold" className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
  );
}
